import React, { useState, useMemo } from 'react';
import { ChartBarIcon, ArrowLeftIcon, HomeIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useContent } from '../hooks/useContent';

const VisitsHistogram = ({ stats }) => {
  const { isDark } = useTheme();
  const { content } = useContent('visitsHistogram');

  const t = content || {
    title: 'Histogramme des visites',
    noData: 'Aucune donnée de visite disponible',
    backTitle: 'Retour',
    backToYearsTitle: 'Retour aux années',
    clickToExplore: 'Cliquez sur une barre pour explorer',
    visitSingular: 'visite',
    visitPlural: 'visites',
    locale: 'fr-FR',
    levels: { years: 'Années', months: 'Mois', weeks: 'Semaines', days: 'Jours' },
    chartTitles: { byYear: 'Visites par année', byMonthPrefix: 'Visites de', byWeekPrefix: 'Semaines de', last7Days: 'Visites des 7 derniers jours' },
  };

  const locale = t.locale || 'fr-FR';

  const [navigationState, setNavigationState] = useState({
    level: 'days',
    selectedYear: null,
    selectedMonth: null,
    selectedWeek: null
  });

  const navigationLevels = {
    years: { label: t.levels.years, next: 'months' },
    months: { label: t.levels.months, next: 'weeks', prev: 'years' },
    weeks: { label: t.levels.weeks, next: 'days', prev: 'months' },
    days: { label: t.levels.days, prev: 'weeks' }
  };

  const navigateToLevel = (level, year = null, month = null, week = null) => {
    setNavigationState({ level, selectedYear: year, selectedMonth: month, selectedWeek: week });
  };

  const goBack = () => {
    const currentLevel = navigationState.level;
    const prevLevel = navigationLevels[currentLevel]?.prev;

    if (currentLevel === 'days' && !navigationState.selectedWeek) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = { year: currentYear, month: today.getMonth() };
      navigateToLevel('weeks', currentYear, currentMonth);
      return;
    }

    if (prevLevel) {
      switch (prevLevel) {
        case 'years':
          navigateToLevel('years');
          break;
        case 'months':
          navigateToLevel('months', navigationState.selectedYear);
          break;
        case 'weeks':
          if (navigationState.selectedYear && navigationState.selectedMonth) {
            navigateToLevel('weeks', navigationState.selectedYear, navigationState.selectedMonth);
          } else if (navigationState.selectedYear) {
            navigateToLevel('months', navigationState.selectedYear);
          } else {
            navigateToLevel('years');
          }
          break;
        default:
          break;
      }
    }
  };

  const goToRoot = () => navigateToLevel('years');

  const handleBarClick = (index, value) => {
    if (value === 0) return;

    const { level, selectedYear, selectedMonth } = navigationState;
    const nextLevel = navigationLevels[level]?.next;

    if (level === 'days' || !nextLevel) return;

    switch (level) {
      case 'years': {
        const yearData = getYearsData();
        navigateToLevel('months', yearData.years[index]);
        break;
      }
      case 'months': {
        const monthData = getMonthsData(selectedYear);
        navigateToLevel('weeks', selectedYear, monthData.months[index]);
        break;
      }
      case 'weeks': {
        const weekData = getWeeksData(selectedYear, selectedMonth);
        navigateToLevel('days', selectedYear, selectedMonth, weekData.weeks[index]);
        break;
      }
      default:
        break;
    }
  };

  const parseVisitDate = (visit) => {
    const dateStr = visit.visitedAt || visit.visited_at || visit.date || visit.createdAt || visit.created_at;
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date;
  };

  const getYearsData = () => {
    if (!stats?.visits) return { data: [], labels: [], years: [] };
    const yearCounts = {};
    stats.visits.forEach(visit => {
      const visitDate = parseVisitDate(visit);
      if (visitDate) {
        const year = visitDate.getFullYear();
        yearCounts[year] = (yearCounts[year] || 0) + 1;
      }
    });
    const years = Object.keys(yearCounts).map(Number).sort().filter(y => !isNaN(y));
    return { data: years.map(y => yearCounts[y]), labels: years.map(y => y.toString()), years };
  };

  const getMonthsData = (year) => {
    if (!stats?.visits || !year) return { data: [], labels: [], months: [] };
    const monthCounts = Array(12).fill(0);
    stats.visits.forEach(visit => {
      const visitDate = parseVisitDate(visit);
      if (visitDate && visitDate.getFullYear() === year) {
        monthCounts[visitDate.getMonth()]++;
      }
    });
    const months = [];
    const data = [];
    const labels = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(year, i, 1);
      months.push({ year, month: i });
      labels.push(date.toLocaleDateString(locale, { month: 'short' }));
      data.push(monthCounts[i]);
    }
    return { data, labels, months };
  };

  const getWeeksData = (year, monthObj) => {
    if (!stats?.visits || !monthObj) return { data: [], labels: [], weeks: [] };
    const today = new Date();
    const weeks = [];
    const data = [];
    const labels = [];

    for (let i = 3; i >= 0; i--) {
      const weekEnd = new Date(today);
      weekEnd.setDate(today.getDate() - (i * 7));
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekEnd.getDate() - 6);

      let weekCount = 0;
      stats.visits.forEach(visit => {
        const visitDate = parseVisitDate(visit);
        if (visitDate) {
          const vd = new Date(visitDate.getFullYear(), visitDate.getMonth(), visitDate.getDate());
          const ws = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
          const we = new Date(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate());
          if (vd >= ws && vd <= we) weekCount++;
        }
      });

      weeks.push({ year: weekStart.getFullYear(), month: weekStart.getMonth(), week: 4 - i, start: new Date(weekStart), end: new Date(weekEnd) });
      labels.push(`S${4 - i}`);
      data.push(weekCount);
    }
    return { data, labels, weeks };
  };

  const getDaysData = (year, monthObj, weekObj) => {
    if (!stats?.visits) return { data: [], labels: [], days: [] };
    const days = [];
    const data = [];
    const labels = [];
    const startDate = weekObj ? new Date(weekObj.start) : (() => { const d = new Date(); d.setDate(d.getDate() - 6); return d; })();

    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const dayStart = new Date(currentDate); dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(currentDate); dayEnd.setHours(23, 59, 59, 999);
      let dayCount = 0;
      stats.visits.forEach(visit => {
        const visitDate = parseVisitDate(visit);
        if (visitDate && visitDate >= dayStart && visitDate <= dayEnd) dayCount++;
      });
      days.push(new Date(currentDate));
      labels.push(currentDate.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' }));
      data.push(dayCount);
    }
    return { data, labels, days };
  };

  const chartData = useMemo(() => {
    if (!stats) return { data: [], labels: [], maxValue: 0 };
    const { level, selectedYear, selectedMonth, selectedWeek } = navigationState;
    let result;
    switch (level) {
      case 'years': result = getYearsData(); break;
      case 'months': result = getMonthsData(selectedYear); break;
      case 'weeks': result = getWeeksData(selectedYear, selectedMonth); break;
      case 'days': result = getDaysData(selectedYear, selectedMonth, selectedWeek); break;
      default: result = { data: [], labels: [] };
    }
    return { ...result, maxValue: Math.max(...(result.data || [0])) };
  }, [stats, navigationState, locale]);

  const getTitle = () => {
    const { level, selectedYear, selectedMonth, selectedWeek } = navigationState;
    switch (level) {
      case 'years': return t.chartTitles.byYear;
      case 'months': return `${t.chartTitles.byMonthPrefix} ${selectedYear}`;
      case 'weeks': {
        const monthName = new Date(selectedYear, selectedMonth.month, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
        return `${t.chartTitles.byWeekPrefix} ${monthName}`;
      }
      case 'days': {
        if (!selectedWeek) return t.chartTitles.last7Days;
        const weekStart = selectedWeek.start.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
        const weekEnd = selectedWeek.end.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
        return `${weekStart} - ${weekEnd}`;
      }
      default: return '';
    }
  };

  const getBreadcrumb = () => {
    const { level, selectedYear, selectedMonth } = navigationState;
    const breadcrumb = [];
    if (level === 'days' && !navigationState.selectedWeek) return breadcrumb;
    if (level !== 'years') {
      breadcrumb.push({ label: t.levels.years, action: () => goToRoot() });
    }
    if ((level === 'weeks' || level === 'days') && selectedYear) {
      breadcrumb.push({ label: selectedYear.toString(), action: () => navigateToLevel('months', selectedYear) });
    }
    if (level === 'days' && selectedMonth) {
      const monthName = new Date(selectedYear, selectedMonth.month, 1).toLocaleDateString(locale, { month: 'long' });
      breadcrumb.push({ label: monthName, action: () => navigateToLevel('weeks', selectedYear, selectedMonth) });
    }
    return breadcrumb;
  };

  if (!stats || chartData.data.length === 0) {
    return (
      <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <h4 className={`text-sm font-medium mb-3 flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <ChartBarIcon className="w-4 h-4 mr-2" />
          {t.title}
        </h4>
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <ChartBarIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>{t.noData}</p>
        </div>
      </div>
    );
  }

  const breadcrumb = getBreadcrumb();
  const canGoBack = navigationLevels[navigationState.level]?.prev;
  const canNavigateDeeper = navigationLevels[navigationState.level]?.next;

  return (
    <div className={`rounded-lg p-4 ${isDark ? 'bg-dark-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
        <div className="flex flex-col mb-2 sm:mb-0">
          <h4 className={`text-sm font-medium flex items-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <ChartBarIcon className="w-4 h-4 mr-2" />
            {getTitle()}
          </h4>
          {breadcrumb.length > 0 && (
            <div className={`flex items-center mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {breadcrumb.map((crumb, index) => (
                <React.Fragment key={index}>
                  <button onClick={crumb.action} className="hover:text-blue-400 transition-colors">{crumb.label}</button>
                  {index < breadcrumb.length - 1 && <span className="mx-1">›</span>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {canGoBack && (
            <button
              onClick={goBack}
              className={`p-2 rounded-md transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-dark-600' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
              title={t.backTitle}
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
          )}
          {navigationState.level !== 'years' && (
            <button
              onClick={goToRoot}
              className={`p-2 rounded-md transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-dark-600' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
              title={t.backToYearsTitle}
            >
              <HomeIcon className="w-4 h-4" />
            </button>
          )}
          {canNavigateDeeper && navigationState.level !== 'days' && (
            <div className="text-xs text-gray-500 hidden sm:block">{t.clickToExplore}</div>
          )}
        </div>
      </div>

      <div className="relative">
        <div className="flex items-end justify-between space-x-1 h-42 mb-2">
          {chartData.data.map((value, index) => {
            const isClickable = canNavigateDeeper && value > 0 && navigationState.level !== 'days';
            return (
              <div key={index} className="flex-1 flex flex-col items-center group">
                <div className="relative w-full">
                  <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 ${isDark ? 'bg-dark-900 text-white' : 'bg-gray-800 text-white'}`}>
                    {value} {value !== 1 ? t.visitPlural : t.visitSingular}
                    {isClickable && <div className="text-xs text-blue-300 mt-1">{t.clickToExplore}</div>}
                  </div>
                  <div
                    className={`w-full rounded-t transition-all duration-300 ${
                      value === 0
                        ? 'bg-gray-600'
                        : isClickable
                          ? 'bg-gradient-to-t from-blue-600 to-blue-400 hover:from-blue-500 hover:to-blue-300 cursor-pointer transform hover:scale-105'
                          : 'bg-gradient-to-t from-blue-600 to-blue-400'
                    }`}
                    style={{ height: `${chartData.maxValue > 0 ? (value / chartData.maxValue) * 120 : 0}px`, minHeight: value === 0 ? '2px' : '3px' }}
                    onClick={() => isClickable && handleBarClick(index, value)}
                  ></div>
                </div>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{value}</p>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between space-x-1">
          {chartData.labels.map((label, index) => (
            <div key={index} className="flex-1 text-center">
              <span className="text-xs text-gray-500 block transform -rotate-45 origin-center">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VisitsHistogram;
